import axios from 'axios';
import { Moment } from 'moment';
import { DateTimeFormat, formatMoment } from '@/shared/converters/dateConverters';
import { BrandId } from '@/shared/models/Brand';
import CommonFilters from '@/shared/models/CommonFilters';
import MobilitySolution from '@/shared/models/MobilitySolution';
import Outlet, { OutletId } from '@/shared/models/Outlet';
import { TagId } from '@/shared/models/Tag';
import { Lookup, present } from '@/shared/utils/typescript';
import BookingAction from '../constants/BookingAction';
import { parseBooking, parseBookings } from '../converters/bookingConverters';
import { parseBookingItems } from '../converters/bookingItemConverters';
import Booking, { BookingId } from '../models/Booking';
import BookingItem from '../models/BookingItem';

const TYPE = 1;

export interface BookingSearchCriteria {
  outletIds: Array<OutletId>;
  tagIds: Array<TagId>;
}

export default class BookingClient {
  static async search(filters: CommonFilters, getOutlet: Lookup<OutletId, Outlet>): Promise<Array<Booking>> {
    const params = {
      moid: filters.activeOutletIds,
      tagid: filters.activeDossierTagIds,
    };

    const response = await axios.get('/api/mobopro/v2/bookings', { params });
    const bookings = parseBookings(response.data);
    bookings.forEach(b => b.outlet = getOutlet(b.outlet.id) ?? b.outlet);
    return bookings;
  }

  static async loadItemsByBrandId(brandId: BrandId, outletId: OutletId): Promise<Array<BookingItem>> {
    const response = await axios.get(`/api/mobopro/v1/bookingitems/planning?brandId=${brandId}&moid=${outletId}`);
    return parseBookingItems(response.data);
  }

  static async loadItems(outletId: OutletId): Promise<Array<BookingItem>> {
    const response = await axios.get('/api/mobopro/v1/bookingItems', {
      params: {
        moid: outletId,
      },
    });
    return parseBookingItems(response.data);
  }

  static async findById(id: BookingId): Promise<Booking> {
    const response = await axios.get(`/api/mobopro/v1/booking/${id}`);
    return present(parseBooking(response.data));
  }

  static async updateAppointment(bookingId: BookingId, appointment: Moment): Promise<void> {
    await axios.patch(`/api/mobopro/v1/booking/${bookingId}`, { appointment: formatMoment(appointment, DateTimeFormat.LEGACY) });
  }

  static async updateOutlet(bookingId: BookingId, outletId: OutletId): Promise<void> {
    await axios.patch(`/api/mobopro/v1/booking/${bookingId}`, { moboOutletId: outletId });
  }

  static async updateMobilitySolution(bookingId: BookingId, mobilitySolution: MobilitySolution | null, waitsOnPremise: boolean): Promise<void> {
    await axios.patch(`/api/mobopro/v1/booking/${bookingId}`, {
      mobilitySolutionId: mobilitySolution?.id ?? null,
      waitsOnPremise,
    });
  }

  static async updateOwnership(id: BookingId, makeOwner: boolean): Promise<Booking> {
    const response = await axios.put('/api/mobopro/v1/bookingownership', { id, type: TYPE, owner: makeOwner });
    return present(parseBooking(response.data));
  }

  /**
   * Update the action that's being taken on a booking
   */
  static async handle(id: BookingId, action: BookingAction): Promise<void> {
    await axios.put('/api/mobopro/v1/bookinghandled', { id, type: TYPE, action });
  }
}